/*
 * Copyright (c) 2018. Viscomp LLC. All rights are reserved!
 */

/**
 * Initialize all audio/video elements on the page.
 */

import { lazyLoad } from "./LazyLoad";
import VideoJs from "./Video/VideoJS";
const IFRAME_DATA_SRC_ATTR = "data-src";

/**
 * return if video provider is youtube or vimeo, based on given url
 * @url - string
 */
function getVideoProvider(url) {
  let youTubeRegex = /https?:\/\/(?:www\.)?youtube\.com\/.*|https?:\/\/(?:www\.)?youtu\.be\/.*|https?:\/\/(?:www\.)?youtube-nocookie\.com\/.*/;
  let vimeoRegex = /https?:\/\/(www\.)?vimeo\.com\/.*|https?:\/\/(www\.)?player\.vimeo\.com\/.*/;
  if (youTubeRegex.test(url)) {
    return "youtube";
  } else if (vimeoRegex.test(url)) {
    return "vimeo";
  }
}
function updateQueryStringParameter(uri, key, value) {
  var re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
  var separator = uri.indexOf("?") !== -1 ? "&" : "?";
  if (uri.match(re)) {
    return uri.replace(re, "$1" + key + "=" + value + "$2");
  } else {
    return uri + separator + key + "=" + value;
  }
}
function initAudioPlayers(context) {
  let audioPlayers = context.querySelectorAll("[data-vplay-audio-player]");
  let loadPlayer = item => {};
  for (let i = 0; i < audioPlayers.length; i++) {
    let item = audioPlayers[i];
    lazyLoad(item, loadPlayer);
  }
  let remoteAudioPlayers = document.querySelectorAll("[data-audio-lazy-load]");
  for (let i = 0, len = remoteAudioPlayers.length; i < len; i += 1) {
    let item = remoteAudioPlayers[i];
    lazyLoad(item, target => {
      target.setAttribute("src", target.getAttribute("data-cms-src"));
    });
  }
}
function initVideoPlayers(context) {
  let videoPlayers = context.querySelectorAll("[data-vplay-video-player]");

  /**
   * Instantiate video player
   *
   * @param {*} item
   */
  let loadPlayer = item => {
    // get data
    let videoPath = item.getAttribute("data-vplay-video-player-videopath");
    let autoplay = item.getAttribute("data-vplay-video-player-autoplay");
    let width = item.getAttribute("data-vplay-video-player-width");
    let height = item.getAttribute("data-vplay-video-player-height");
    let poster = item.getAttribute("data-vplay-video-player-poster");
    let hideControls = item.getAttribute("data-vplay-video-player-hidecontrols");
    let isFluid = item.getAttribute("data-vplay-video-player-isFluid");
    let customClass = item.getAttribute("data-vplay-custom-class");
    let loop = item.getAttribute("data-vplay-video-player-loop");
    let htmlAttrs = item.getAttribute("data-vplay-video-player-html-attributes") !== null ? JSON.parse(item.getAttribute("data-vplay-video-player-html-attributes")) : {};
    let videoSettings = {};
    videoSettings.selector = item;
    videoSettings.showDownloadBtn = false;
    if (autoplay !== null) {
      videoSettings.autoplay = true;
    }
    if (poster) {
      videoSettings.poster = poster;
    }
    if (width && width !== "0") {
      videoSettings.width = width;
    }
    if (height && height !== "0") {
      videoSettings.height = height;
    }
    if (hideControls !== void 0 && Object.prototype.toString.call(hideControls) === "[object String]") {
      videoSettings.hideControls = hideControls === "true";
    }
    if (isFluid && isFluid == "true") {
      videoSettings.fluid = true;
    } else {
      videoSettings.fluid = false;
    }
    if (loop && loop == "true") {
      videoSettings.loop = true;
    } else {
      videoSettings.loop = false;
    }
    if (customClass) {
      videoSettings.customClass = customClass;
    }
    videoSettings.path = videoPath;
    videoSettings.preload = "metadata";
    VideoJs(videoSettings).then(player => {
      // get video tags
      let videoTag = player.$("video");
      Object.keys(htmlAttrs).forEach(attrName => {
        videoTag.setAttribute(attrName, htmlAttrs[attrName]);
      });
    });
  };
  for (let i = 0; i < videoPlayers.length; i++) {
    let item = videoPlayers[i];
    if (item.getAttribute('data-disable-lazy-loading') === 'true') {
      loadPlayer(item);
    } else {
      lazyLoad(item, loadPlayer);
    }
  }
}
function initRemotePlayers(context, source) {
  let videoPlayers = context.querySelectorAll('[data-vplay-remote-video-player]');
  let loadPlayer = item => {
    let iframe = item.querySelector('iframe');

    //if conditional option is not set,
    if (item.getAttribute('data-vplay-conditional-rendering') === null) {
      // set src attribute to iframe tag , takem from data-src attribute
      iframe.setAttribute('src', iframe.getAttribute(IFRAME_DATA_SRC_ATTR));
    }
    if (source === "LE_NEWSFEED") {
      iframe.setAttribute("src", iframe.getAttribute(IFRAME_DATA_SRC_ATTR));
    }

    // get play button
    let playButton = item.querySelector("[data-youtube-playbtn-wp]");
    if (playButton) {
      playButton.onclick = () => {
        let iframeUrl = iframe.getAttribute("src");
        if (iframeUrl === null) {
          console.error("Missing 'src' iframe's attribute");
          return;
        }
        iframeUrl = updateQueryStringParameter(iframeUrl, "autoplay", "1");
        iframe.setAttribute("src", iframeUrl);
        playButton.style.display = "none";
        if (item.querySelector("[data-youtube-poster-wp]")) {
          item.querySelector("[data-youtube-poster-wp]").style.display = "none";
        }
      };
    }
  };

  // for all players
  for (let i = 0; i < videoPlayers.length; ++i) {
    let item = videoPlayers[i];
    if (item.getAttribute("data-disable-lazy-loading") !== "true") {
      // lazy loading is not disabled and there is no conditional rendering activated, so lazy load the player
      lazyLoad(item, loadPlayer);
    }
    if (item.getAttribute('data-disable-lazy-loading') === 'true') {
      // lazy loading is disabled and there is no conditional rendering activated, so load the player straight away
      loadPlayer(item);
    }
  }
}
const initPlayers = function () {
  /*
      Initialize audio players
   */
  initAudioPlayers(document);

  /*
      Initialize video players
   */
  initVideoPlayers(document);

  /*
      Initialize remote video players
  */
  initRemotePlayers(document);
};
export { initRemotePlayers, initVideoPlayers, initAudioPlayers, initPlayers };